import React, { useEffect, useState } from "react";
import { Gift, Copy, Users, Wallet } from "lucide-react";
import axios from "axios";

export default function Refer() {
  const [referralCode, setReferralCode] = useState(() => {
    const saved = localStorage.getItem("referralCode");
    return saved ? saved : null;
  });

  const [earnedBalance, setEarnedBalance] = useState(() => {
    const saved = localStorage.getItem("earnedBalance");
    return saved ? parseFloat(saved) : 0;
  });

  const [totalReferrals, setTotalReferrals] = useState(() => {
    const saved = localStorage.getItem("totalReferrals");
    return saved ? parseInt(saved) : 0;
  });

  const [copied, setCopied] = useState(false);

  // 🔹 Fetch referral info with background update
  useEffect(() => {
    const fetchReferral = async () => {
      try {
        const res = await axios.get("https://api.speedfollowinfo.com/api/refer", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        if (res.data.success) {
          const code = res.data.referralCode || null;
          const balance = res.data.earnedBalance || 0;
          const total = res.data.totalReferrals || 0;

          // Update state
          setReferralCode(code);
          setEarnedBalance(balance);
          setTotalReferrals(total);

          // Update localStorage
          localStorage.setItem("referralCode", code);
          localStorage.setItem("earnedBalance", balance);
          localStorage.setItem("totalReferrals", total);
        }
      } catch (err) {
        console.error("Error fetching referral data:", err);
      }
    };

    fetchReferral();
  }, []);

  const referralLink = `https://speedfollowinfo.com/register?ref=${referralCode}`;

  // 🔹 Copy referral link
  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // 🔹 Create referral code for old users
  const handleCreateCode = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        "https://api.speedfollowinfo.com/api/refer/create",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        setReferralCode(res.data.referralCode);
        localStorage.setItem("referralCode", res.data.referralCode);
      }
    } catch (err) {
      console.error("Error creating referral code:", err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f1a] text-white px-6 py-10 pt-24">
      {/* Header */}
      <div className="max-w-3xl mx-auto text-center mb-10">
        <div className="flex justify-center mb-4">
          <div className="p-4 rounded-full bg-[#1b2142] shadow-lg">
            <Gift size={40} className="text-[#f2a900]" />
          </div>
        </div>

        <h1 className="text-4xl font-extrabold mb-3 text-[#facc15]">
          Refer & Earn
        </h1>

        <p className="text-gray-300 text-lg">
          বন্ধুদের ইনভাইট করো এবং প্রতিটা সফল রেজিস্ট্রেশনে ও ডিপোজিট এ ইনকাম করো 💸
        </p>
      </div>

      {/* Referral Box */}
      <div className="max-w-2xl mx-auto bg-[#111827] border border-gray-800 rounded-2xl p-6 shadow-xl">
        <h2 className="text-xl font-bold mb-4 text-[#00eaff]">
          🔗 Your Reference Code
        </h2>

        <div className="flex items-center gap-3 bg-[#0b0f1a] border border-gray-700 rounded-lg p-3">
          <input
            type="text"
            readOnly
            value={referralCode ? referralLink : ""}
            placeholder="No referral code yet"
            className="flex-1 bg-transparent text-gray-300 outline-none text-sm"
          />

          {referralCode ? (
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 bg-[#f2a900] text-black px-4 py-2 rounded-lg font-bold hover:opacity-90 transition"
            >
              <Copy size={18} />
              Copy
            </button>
          ) : (
            <button
              onClick={handleCreateCode}
              className="flex items-center gap-2 bg-[#00eaff] text-black px-4 py-2 rounded-lg font-bold hover:opacity-90 transition"
            >
              Create Code
            </button>
          )}
        </div>

        {copied && (
          <p className="mt-3 text-green-400 font-semibold text-sm">
            ✅ Referral link copied successfully!
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="max-w-4xl mx-auto mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-[#111827] border border-gray-800 rounded-xl p-5 flex items-center gap-4">
          <Users className="text-[#8b5cf6]" size={36} />
          <div>
            <p className="text-gray-400 text-sm">Total Referrals</p>
            <p className="text-2xl font-bold">{totalReferrals}</p>
          </div>
        </div>

        <div className="bg-[#111827] border border-gray-800 rounded-xl p-5 flex items-center gap-4">
          <Wallet className="text-green-400" size={36} />
          <div>
            <p className="text-gray-400 text-sm">Earned Balance</p>
            <p className="text-2xl font-bold">৳{earnedBalance.toFixed(2)}</p>
          </div>
        </div>

        <div className="bg-[#111827] border border-gray-800 rounded-xl p-5 flex items-center gap-4">
          <Gift className="text-[#f2a900]" size={36} />
          <div>
            <p className="text-gray-400 text-sm">Bonus per Referral</p>
            <p className="text-2xl font-bold">৳10</p>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="max-w-2xl mx-auto mt-10 bg-[#0f172a] border border-gray-700 rounded-xl p-5 text-gray-300 text-sm leading-relaxed">
        <p className="mb-2">
          🌐 প্রতি রেফার এ ইউজার ডিপোজিট করলে আপনি ১০ টাকা পাবেন, ডিপোজিট ছাড়া পাবেন না 🚫
        </p>
        <p className="mb-2">
          ✅ আপনার রেফার লিংক দিয়ে নতুন ইউজার রেজিস্টার করলে আপনি বোনাস পাবেন।
        </p>
        <p className="mb-2">
          ⚠️ একাধিক ফেক একাউন্ট বা নিজের রেফার নিজে করলে বোনাস দেওয়া হবে না।
        </p>
        <p>
          💰 বোনাস ব্যালেন্স সরাসরি আপনার Earned Balance এ যোগ হবে।
        </p>
      </div>
    </div>
  );
}